/* @flow */
import {fetchList} from 'nutshell-core/entities/fetch-list';
import {transformDataForReport} from 'nutshell-core/json-to-report-data';
import {safelyParseError} from 'nutshell-core/utils';
import type {ActionsObservable} from 'redux-observable';
import {Observable} from 'rxjs';
import {switchMap, merge, filter, map, catchError} from 'rxjs/operators';

import {getMembershipReportPostParams} from './audience-utils';
import {
    updateAudienceMemberListData,
    failAudienceMemberListData,
    updateAudienceMembershipReportTableData,
    updateAudienceMembershipReportChartData,
    failAudienceMembershipReportChartData,
    type AudienceMembershipReportDataRequestedAction,
    type AudienceMemberListRequestedAction,
} from './audiences-actions';

export const requestAudienceMemberListEpic = (action$: ActionsObservable<*>) =>
    action$
        .ofType('AUDIENCE_MEMBER_LIST_DATA_REQUESTED')
        .switchMap((action: AudienceMemberListRequestedAction) => {
            const {filters, pageNum = 1, columns, sort, q} = action.payload;

            // Our pagination components aren't 0-based, but this specific
            // request expects page 0 to be the first page. Using Math.max() here
            // in case older URLs with page 0 are still in the wild
            const adjustedPageNum = Math.max(0, pageNum - 1);

            const listRequestStream = Observable.fromPromise(
                fetchList('contacts', {
                    filter: filters,
                    sort,
                    q,
                    page: {page: adjustedPageNum},
                    fields: columns,
                })
            );

            const audienceMemberListDataStream = listRequestStream.map((response) =>
                updateAudienceMemberListData(response)
            );

            return audienceMemberListDataStream.catch((err) => {
                const safeError = safelyParseError(err);

                return Observable.of(failAudienceMemberListData(safeError));
            });
        });

export const requestAudienceMembershipReportDataEpic = (action$: ActionsObservable<*>) =>
    action$.ofType('AUDIENCE_MEMBERSHIP_REPORT_DATA_REQUESTED').pipe(
        switchMap((action: AudienceMembershipReportDataRequestedAction) => {
            const {reportParams, filters} = action.payload;
            const params = getMembershipReportPostParams(reportParams, filters);

            const requestStream = Observable.from(fetchData(params));
            const reportDataStream = requestStream.pipe(map(transformDataForReport));

            const reportTableDataStream = reportDataStream.pipe(
                map(({tableData}) => updateAudienceMembershipReportTableData(tableData))
            );

            const reportChartDataStream = reportDataStream.pipe(
                map((data) => data.chartData),
                filter((chartData) => Boolean(chartData)),
                map((chartData) => updateAudienceMembershipReportChartData({chartData}))
            );

            return reportChartDataStream.pipe(
                merge(reportTableDataStream),
                catchError((err) => Observable.of(failAudienceMembershipReportChartData(err)))
            );
        })
    );

function fetchData(params) {
    return Promise.all([fetchAudienceMembershipReportData(params)]);
}

function fetchAudienceMembershipReportData(params: Object): Promise<any> {
    const url = '/rest/audiences/report';
    const ajaxParams = {
        url,
        dataType: 'json',
        data: params,
    };

    return ($.ajax(ajaxParams): any); // This is how we have to convince flow that the response is a ReportsResponse
}
